import { RefObject, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { AppDispatch } from 'app/store';

import NumberInputWithTwoDecimals from 'features/forms/components/NumberInputWithTwoDecimals';

import {
  getVesselBunkeringDetails,
  selectVesselBunkeringDetailsData,
  VesselBunkeringDetailsData,
} from '../../../slices/portCallSlice';

import BunkeringCheckboxGroup from '../../../../forms/components/BunkeringCheckboxGroup';
import Input from '../../../../forms/components/Input';
import RadioButtons from '../../../../forms/components/RadioButtonsCustom';
import { booleanOptionsForRadioGroup } from '../../../../forms/helpers/random-form-helpers';
import { FormRefContext } from '../../../../global/context/FormRefContext';

import '../../../styles/bunkering-detail.scss';

const validationSchema = Yup.object({
  purposeOfCall: Yup.string(),
});

const booleanOptionsForRadio = booleanOptionsForRadioGroup();
const verticalBunkeringManifoldOpts = [
  { key: 'Aft', value: 'AFT' },
  { key: 'Mid', value: 'MID' },
  { key: 'Forward', value: 'FORWARD' },
  { key: 'Superstr.', value: 'SUPERSTRUCTURE' },
];
const horizontalBunkeringManifoldOpts = [
  { key: 'Port', value: 'PORT' },
  { key: 'Centre', value: 'CENTRE' },
  { key: 'Starb.', value: 'STARBOARD' },
];

const bunkeringManifoldConnectionTypes = [
  { key: 'ANSI', value: 'ANSI' },
  { key: 'DIN', value: 'DIN' },
];

const BunkeringDetails = ({
  ref,
}: {
  ref: RefObject<FormikProps<VesselBunkeringDetailsData>>;
}) => {
  const { formContextRef } = useContext(FormRefContext);
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams();
  const bunkeringDetailsData = useSelector(selectVesselBunkeringDetailsData);

  useEffect(() => {
    if (params.uid) {
      dispatch(getVesselBunkeringDetails(params.uid));
    }
  }, [dispatch, params.uid]);

  useEffect(() => {
    formContextRef.current = ref.current;
  }, [formContextRef, ref]);

  const handleSubmit = () => {};

  return (
    <div className="bunkering-detail">
      <div className="bunkering-detail__title">
        <h3>Bunkering</h3>
        <p>Enter bunkering supply details</p>
      </div>
      <Formik
        innerRef={ref}
        onSubmit={handleSubmit}
        enableReinitialize
        initialValues={bunkeringDetailsData}
        validationSchema={validationSchema}>
        <Form
          className="bunkering-detail-form"
          data-testid="bunkering-detail-form">
          <NumberInputWithTwoDecimals
            name="rateReceptionPerHour"
            placeholder="Rate reception per hour (m3/h)"
            label="Rate reception per hour (m3/h)"
          />
          <NumberInputWithTwoDecimals
            name="maxPressure"
            placeholder="Max pressure (bar)"
            label="Max pressure (bar)"
          />
          <NumberInputWithTwoDecimals
            name="diameterConnection"
            placeholder="Diameter connection (cm)"
            label="Diameter connection (cm)"
          />
          <NumberInputWithTwoDecimals
            name="freeBoardOnArrival"
            placeholder="Free board on arrival (m)"
            label="Free board on arrival (m)"
          />
          <Input
            name="bollardsLocationFromStern"
            placeholder="Bollards location from stern"
            label="Bollards location from stern"
          />
          <RadioButtons
            name="isVesselFittedWithScrubber"
            label="Is the vessel fitted with a scrubber?"
            options={booleanOptionsForRadio}
          />
          <RadioButtons
            name="canReceiveDifferentGradesSimultaneously"
            label="Can you receive grades simultaneously?"
            options={booleanOptionsForRadio}
          />
          <RadioButtons
            name="canReceiveDifferentProductsSimultaneously"
            label="Can you receive different products simultaneously?"
            options={booleanOptionsForRadio}
          />
          <RadioButtons
            name="isDeckCraneAvailable"
            label="Do you have a deck crane available to lift barge hoses?"
            options={booleanOptionsForRadio}
          />
          <RadioButtons
            name="haveCertifiedPersonnelToAccessBarge"
            label="Available certified personnel transfer basket for barge access?"
            options={booleanOptionsForRadio}
          />
          <BunkeringCheckboxGroup
            name="bunkerManifold.situation.manifoldPosition1"
            label="Manifold position (horizontal)"
            options={horizontalBunkeringManifoldOpts}
          />
          <BunkeringCheckboxGroup
            name="bunkerManifold.situation.manifoldPosition2"
            label="Manifold position (vertical)"
            options={verticalBunkeringManifoldOpts}
          />
          <NumberInputWithTwoDecimals
            name="bunkerManifold.situation.distanceFromStern"
            placeholder="Distance from stern"
            label="Manifold distance from stern"
          />
          <NumberInputWithTwoDecimals
            name="bunkerManifold.situation.heightAboveWaterline"
            placeholder="Height above waterline"
            label="Manifold height above waterline"
          />
          <NumberInputWithTwoDecimals
            name="bunkerManifold.situation.distanceFromPortSide"
            placeholder="Distance from port side"
            label="Manifold distance from port side"
          />
          <div />
          <RadioButtons
            name="bunkerManifold.connectionType"
            label="Type of manifold connection flange"
            options={bunkeringManifoldConnectionTypes}
          />
          <Input
            name="bunkerManifold.connectionSize"
            label="Size of manifold connection"
            placeholder="Size of connection"
          />
        </Form>
      </Formik>
    </div>
  );
};

export default BunkeringDetails;
